'use client'

import Spinner from '@/shared/components/Spinner'
import React, { ReactNode, useState } from 'react'
import TextButton from '@/shared/components/buttons/TextButton'
import { useTwilioWidget } from '@/features/twilio/hooks/useTwilioWidget'
import clsx from 'clsx'
import { TwilioContext, useStore } from '@/lib/store'
import DeviceSettings from './components/DeviceSettings'
import CallScreen, { CallScreenStatus } from './components/CallScreen'
import { useDeviceSettings } from '@/features/twilio/hooks/useDeviceSettings'
import useInterval from '@/shared/hooks/useInterval'
import { Session } from 'next-auth'

type Props = {
  session: Session | null
}

export default function TwilioContainer({ session }: Props) {
  const {
    twilioState,
    hideWidget,
    twilioContext,
    endCall,
    muteCall,
    unmuteCall,
  } = useTwilioWidget(session)

  const setTwilioState = useStore((state) => state.setTwilioState)
  const changeOutputDevice = useStore((state) => state.changeOutputDevice)
  const changeInputDevice = useStore((state) => state.changeInputDevice)

  function closeModal() {
    hideWidget()
  }

  function closeWidget() {
    setTwilioState({ state: 'HIDDEN' })
  }

  switch (twilioState.state) {
    case 'ASKING_PERMISSION':
      return (
        <FixedModal>
          <h3 className="text-md text-center font-semibold leading-6 text-gray-700">
            Establishing connection and accessing microphone
          </h3>
          <Spinner size={10} />
        </FixedModal>
      )
    case 'PERMISSION_DENIED':
      return (
        <FixedModal>
          <h3 className="text-md font-semibold leading-6 text-red-500">
            Microphone access denied
          </h3>
          <p className="text-sm leading-6 tracking-tight text-gray-600">
            You need to allow microphone access in your browser setting before
            you can call patients through our website.
          </p>
          <TextButton
            title="Close"
            color="red"
            appearance="primary"
            className="w-full"
            onClick={closeModal}
          />
        </FixedModal>
      )
    case 'SELECT_IO_DEVICES':
      return (
        <DeviceSelectionMenu
          onContinue={twilioState.onContinue}
          twilioContext={twilioContext}
          changeOutputDevice={changeOutputDevice}
          changeInputDevice={changeInputDevice}
        />
      )

    case 'CALL_SCREEN':
      return (
        <CallScreenContainer
          endCall={endCall}
          twilioContext={twilioContext!!}
          closeWidget={closeWidget}
          isMuted={
            (twilioContext?.call?.status === 'CONNECTED' &&
              twilioContext?.call?.muted) ||
            false
          }
          muteCall={muteCall}
          unmuteCall={unmuteCall}
          changeOutputDevice={changeOutputDevice}
          changeInputDevice={changeInputDevice}
        />
      )
    default:
      return null
  }
}

type FixedModalProps = {
  width?: string
  children?: ReactNode
}

function FixedModal({ children, width = 'sm:max-w-sm' }: FixedModalProps) {
  return (
    <div className="fixed inset-0 z-50 w-screen overflow-y-auto bg-gray-500 bg-opacity-75 transition-opacity">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className={clsx(
            'relative overflow-hidden rounded-lg bg-white px-4 py-5 text-left shadow-xl transition-all sm:w-full',
            width,
          )}
        >
          <div className="flex flex-col items-center justify-center gap-y-5">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}

type DeviceSelectionMenuProps = {
  twilioContext: TwilioContext | null
  changeOutputDevice: (deviceId: string) => void
  changeInputDevice: (deviceId: string) => void
  onContinue: () => void
}

function DeviceSelectionMenu({
  twilioContext,
  changeOutputDevice,
  changeInputDevice,
  onContinue,
}: DeviceSelectionMenuProps) {
  return (
    <FixedModal width="sm:max-w-lg">
      <h3 className="text-md font-semibold leading-6 text-gray-700">
        Select your devices
      </h3>
      {twilioContext ? (
        <>
          <DeviceSettingsContainer
            twilioContext={twilioContext}
            changeOutputDevice={changeOutputDevice}
            changeInputDevice={changeInputDevice}
          />
          <div className="flex items-center">
            <TextButton title="Continue" color="blue" onClick={onContinue} />
          </div>
        </>
      ) : (
        <Spinner size={10} />
      )}
    </FixedModal>
  )
}

type DeviceSettingsContainerProps = {
  twilioContext: TwilioContext
  changeOutputDevice: (deviceId: string) => void
  changeInputDevice: (deviceId: string) => void
}

function DeviceSettingsContainer({
  twilioContext,
  changeOutputDevice,
  changeInputDevice,
}: DeviceSettingsContainerProps) {
  const settings = useDeviceSettings(
    twilioContext,
    changeOutputDevice,
    changeInputDevice,
  )

  return (
    <>
      <DeviceSettings {...settings} />
    </>
  )
}

type CallScreenContainerProps = {
  endCall: () => void
  twilioContext: TwilioContext
  changeOutputDevice: (deviceId: string) => void
  changeInputDevice: (deviceId: string) => void
  closeWidget: () => void
  isMuted: boolean
  muteCall: () => void
  unmuteCall: () => void
}

function CallScreenContainer({
  endCall,
  twilioContext,
  changeOutputDevice,
  changeInputDevice,
  closeWidget,
  muteCall,
  unmuteCall,
}: CallScreenContainerProps) {
  const [callElapsedMs, setCallElapsedMs] = useState(0)
  useInterval(
    () =>
      setCallElapsedMs(
        Date.now() -
          (twilioContext.call?.status === 'CONNECTED'
            ? twilioContext.call.startTime
            : 0),
      ),
    twilioContext.call?.status === 'CONNECTED' ? 500 : undefined,
  )

  const settings = useDeviceSettings(
    twilioContext,
    changeOutputDevice,
    changeInputDevice,
  )

  let status: CallScreenStatus = { status: 'IDLE' }
  switch (twilioContext.call?.status) {
    case 'CONNECTING':
      status = { status: 'LOADING' }
      break
    case 'CONNECTED':
      status = { status: 'IN_PROGRESS', elapsedMs: callElapsedMs, metadata: [] }
      break
  }

  return (
    <CallScreen
      status={status}
      closeable={!twilioContext.call}
      onClose={closeWidget}
      isMuted={
        (twilioContext.call?.status === 'CONNECTED' &&
          twilioContext.call?.muted) ||
        false
      }
      mute={muteCall}
      unmute={unmuteCall}
      endCall={endCall}
      {...settings}
    />
  )
}
